import React, { useState, useContext, useEffect, useMemo, useRef } from 'react'
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { OperatorContext } from '../../contexts/OperatorContext';
import { HospitalContext } from '../../contexts/HospitalContext';
import { UserContext } from '../../contexts/UserContext';
import OperatorActionErrorModal from './OperatorActionErrorModal';
import PasswordInput from '../common/PasswordInput';
import PasswordRequirements from '../common/PasswordRequirements';
import { validatePassword } from '../../utils/validatePassword';
import Loader from '../Loader';

import './CMSForm.css'
window.Buffer = window.Buffer || require("buffer").Buffer;

const ROLE_OPTIONS = ['Owner', 'Admin', 'Listing Admin', 'User'];
const displayRole = (role) => String(role || '').toLowerCase() === 'manager' ? 'Listing Admin' : (role || '')

const ManageUserForm = ({ closeForm, initialData, search }) => {
    const { getAllOperators } = useContext(OperatorContext)
    const { getHospitals } = useContext(HospitalContext)
    const { createUser, editUser, editUserPassword } = useContext(UserContext)

    const [hospitals, setHospitals] = useState([])
    const [operators, setOperators] = useState([])
    const [editPassword, setEditPassword] = useState(false)
    const [error, setError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errorModal, setErrorModal] = useState(null)
    const [siteAdmin, setSiteAdmin] = useState(false)
    const [inputData, setInputData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        position: '',
        password: '',
        confirm_password: ''
    })
    const [organizations, setOrganizations] = useState([])
    const loadedUserIdRef = useRef('')
    const removedLoadedIdsRef = useRef(new Set())
    const createdUserIdRef = useRef('')
    const createResponseMissingIdRef = useRef(false)

    const isEditing = initialData && Object.keys(initialData).length > 0

    const allOrganizations = useMemo(() => [
        ...(operators || []).map(o => ({ ...o, _type: o.organization_type || 'operator' })),
        ...(hospitals || []).map(h => ({ ...h, _type: h.organization_type || 'hospital' })),
    ].sort((a, b) => a.name.toString().localeCompare(b.name.toString())), [operators, hospitals])

    useEffect(() => {
        getAllOperators(1, null, 'all').then((data) => { if (data) setOperators(data) })
        getHospitals(1, null, 'all').then((data) => { if (data) setHospitals(data) })
    }, [])

    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0 && allOrganizations.length > 0) {
            const userId = String(initialData._id || '')
            // Same-user catalog refresh keeps hops; a new edited user resets from initialData.
            const firstLoad = !loadedUserIdRef.current
            const userChanged = !!loadedUserIdRef.current && loadedUserIdRef.current !== userId
            loadedUserIdRef.current = userId
            if (firstLoad || userChanged) {
                setInputData({
                    first_name: initialData.first_name,
                    last_name: initialData.last_name,
                    email: initialData.email,
                    position: initialData.position || '',
                })
                setSiteAdmin(!!initialData.site_admin)
            }
            if (userChanged) {
                removedLoadedIdsRef.current = new Set()
                createResponseMissingIdRef.current = false
            }
            if (createdUserIdRef.current && createdUserIdRef.current !== userId) createdUserIdRef.current = ''

            setOrganizations(prev => {
                const extras = userChanged ? [] : prev.filter(row => row._isNew && !row._loadedId)
                if (!initialData.organizations?.length) return extras
                const mapped = initialData.organizations
                    .map(org => {
                        const match = allOrganizations.find(o => String(o._id) === String(org.organization_id))
                        if (!match) return null
                        const loadedId = String(org.organization_id)
                        // Same-user catalog refresh remaps from initialData; don't re-stamp a loaded row the user Removed.
                        if (!userChanged && removedLoadedIdsRef.current.has(loadedId)) return null
                        const inProgress = userChanged ? null : prev.find(row => String(row._loadedId) === loadedId)
                        return inProgress || {
                            organization_id: loadedId,
                            role: org.role || '',
                            _type: match._type,
                            _loadedId: loadedId,
                        }
                    })
                    .filter(Boolean)
                return [...mapped, ...extras]
            })
        }
    }, [initialData, allOrganizations])

    function handleChange(e) {
        const { name, value } = e.target;
        setInputData({ ...inputData, [name]: value });
    }

    function handleAddOrganization() {
        if (extraOrgRow) return
        setOrganizations([...organizations, { organization_id: '', role: '', _isNew: true }])
    }

    function handleRemoveOrganization(index) {
        const loadedId = String(organizations[index]?._loadedId || '')
        if (loadedId) removedLoadedIdsRef.current.add(loadedId)
        setOrganizations(organizations.filter((_, i) => i !== index))
    }

    function handleOrgChange(index, field, value) {
        const updated = [...organizations]
        if (field === 'organization') {
            const org = allOrganizations.find(o => String(o._id) === String(value))
            const nextId = org ? String(org._id) : ''
            const loadedId = String(updated[index]._loadedId || '')
            updated[index] = {
                ...updated[index],
                organization_id: nextId,
                _type: org?._type || '',
                role: org?._type === 'hospital' ? '' : updated[index].role,
                // Hop-back to this row's loaded org drops extra so save overlays prev. "+" extra stays extra.
                _isNew: (updated[index]._isNew && !updated[index].organization_id) || nextId !== loadedId,
            }
        } else {
            updated[index] = { ...updated[index], [field]: value }
        }
        setOrganizations(updated)
    }

    function getAvailableOrganizations(currentIndex) {
        const assignedIds = organizations
            .filter((_, i) => i !== currentIndex)
            .map(org => String(org.organization_id))
        return allOrganizations.filter(o => !assignedIds.includes(String(o._id)))
    }

    // First newly added complete org, else first existing membership whose role changed or hopped back to _loadedId.
    // ponytail: server still applies one organization_id per request (register-cms / PUT /user/:id).
    function getSelectedOrg() {
        const completed = organizations.filter(org => org.organization_id)
        const existing = initialData?.organizations || []
        const existingIds = existing.map(o => String(o.organization_id))
        // "+" rows and org-switched rows are extra even if the id was on the user (remove/re-add).
        const added = completed.find(org => org._isNew || !existingIds.includes(String(org.organization_id)))
        if (added) return added
        return completed.find(org => {
            const prev = existing.find(o => String(o.organization_id) === String(org.organization_id))
            if (!prev) return false
            if ((org.role || '') !== (prev.role || '')) return true
            // Hop-back drops _isNew via _loadedId (false, not missing); overlay restores link_access / flags / listing access.
            return org._isNew === false && String(org._loadedId) === String(org.organization_id)
        }) || null
    }

    // Extra "+" row, else first dirty existing membership — API takes one organization_id.
    const extraOrgRow = organizations.find(org => org._isNew || !org.organization_id) || getSelectedOrg()

    function getRemovedOrgIds() {
        const remaining = new Set(
            organizations.filter(org => org.organization_id).map(org => String(org.organization_id))
        )
        return (initialData?.organizations || [])
            .map(org => String(org.organization_id))
            .filter(id => id && allOrganizations.some(o => String(o._id) === id) && !remaining.has(id))
    }

    async function handleClick(additionalFlags = {}) {
        if (createResponseMissingIdRef.current) {
            setError('The user may have been saved, but no user ID was returned. Close this form and reopen the user before retrying.')
            return
        }
        setError('')
        if (!isValidated()) return
        const first_name = inputData.first_name.trim()
        const last_name = inputData.last_name.trim()
        const email = inputData.email.trim()
        const position = (inputData.position || '').trim()
        setInputData({ ...inputData, first_name, last_name, email, position })
        setIsSubmitting(true)
        try {
            let data = { ...inputData, ...additionalFlags }
            delete data.confirm_password
            data.first_name = first_name
            data.last_name = last_name
            data.email = email
            data.position = position

            data.site_admin = siteAdmin
            const selectedOrg = getSelectedOrg()
            if (selectedOrg) {
                // Hopped rows stay extra — don't overlay the previous org's membership/listing access.
                const prev = selectedOrg._isNew ? null : (initialData?.organizations || []).find(
                    o => String(o.organization_id) === String(selectedOrg.organization_id)
                )
                // PUT replaces the org entry; overlay same-org membership. No listing Select — don't send A's listings as B's.
                const { _id, id, listing_access, ...membership } = prev || {}
                Object.assign(data, membership)
                data.organization_id = String(selectedOrg.organization_id)
                if (selectedOrg.role) data.role = selectedOrg.role
                if (prev && listing_access?.length) data.organization_listings = listing_access
            }
            if (isEditing) {
                const removedOrgIds = getRemovedOrgIds()
                if (removedOrgIds.length) data.remove_organization_ids = removedOrgIds
                await editUser(initialData._id, data, search)

                if (editPassword) {
                    await editUserPassword(initialData._id, data, search)
                }
            } else if (createdUserIdRef.current) {
                await editUser(createdUserIdRef.current, data, search)
            } else {
                const response = await createUser(data, search)
                const createdId = response?.data?.user?._id || response?.data?.user?.id
                if (!createdId) {
                    createResponseMissingIdRef.current = true
                    setError('Failed to save user.')
                    return
                }
                createdUserIdRef.current = String(createdId)
                if (data.organization_id) {
                    const attached = (response?.data?.organizations || []).some(
                        o => String(o.organization_id) === String(data.organization_id)
                    )
                    if (!attached) {
                        setError('User was saved but was not added to the organization.')
                        return
                    }
                }
            }

            // Hop-back leaves `_isNew: false` (dirty); drop it so a later save skips overlay PUT.
            setOrganizations(prev => prev.map(({ _isNew, ...org }) => org))
            setErrorModal(null)
            closeForm()
        } catch (error) {
            console.log(error)
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                if (errorData.error === 'organization_has_active_subscriptions' ||
                    errorData.error === 'operator_has_active_subscriptions' ||
                    errorData.error === 'require_move_and_delete_organization' ||
                    errorData.error === 'organization_has_assigned_users' ||
                    errorData.error === 'operator_has_assigned_users') {
                    setErrorModal(errorData);
                    return;
                }
                if (errorData.errors?.length) {
                    setError(errorData.errors.join(' '));
                    return;
                }
                if (errorData.msg) {
                    setError(errorData.msg);
                    return;
                }
            }
            setError('Failed to save user.');
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleRetry = () => {
        if (extraOrgRow && (!extraOrgRow.organization_id || (extraOrgRow._type !== 'hospital' && !extraOrgRow.role))) return
        if (!isValidated()) return
        handleClick()
    }
    const handleRetryWithCancelSubscription = () => handleClick({ cancel_active_subscription: true });
    const handleMoveAndDeleteOperator = () => handleClick({ move_and_delete_organization: true });
    // Unchecking a membership can leave an organization with no users. The server rejects
    // that unless told which outcome is wanted, so offer keeping the empty organization
    // alongside deleting it rather than forcing the delete.
    const handleKeepOrganization = () => handleClick({ keep_organization: true });
    const handleCancelErrorModal = () => setErrorModal(null);

    const isValidated = () => {
        if (!inputData.first_name?.trim() || !inputData.last_name?.trim() || !inputData.email?.trim() || !inputData.position?.trim()) {
            setError('Please fill out all required fields.')
            return false
        }
        if (!emailCheck(inputData.email.trim())) {
            setError('Invalid Email')
            return false
        }
        // Pin means persist will editUser; don't re-require create password.
        if ((!isEditing && !createdUserIdRef.current) || editPassword) {
            if (!validatePassword(inputData.password)) {
                setError('Password does not meet the requirements.')
                return false
            }
            if (inputData.password !== inputData.confirm_password) {
                setError('Passwords do not match.')
                return false
            }
        }
        // Empty "+" row is not a membership; getSelectedOrg skips it, so save would ignore it.
        if (extraOrgRow && !extraOrgRow.organization_id) {
            setError('Select an organization.')
            return false
        }
        const selectedOrg = getSelectedOrg()
        if (selectedOrg && selectedOrg._type !== 'hospital' && !selectedOrg.role) {
            setError('Select a role.')
            return false
        }
        return true
    }

    function emailCheck(email) {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    function handleCloseForm(e) {
        // mousedown (not click): a failed save unmounts the spinner under the cursor,
        // and the leftover click would otherwise hit this overlay and dismiss the form.
        if (isSubmitting) return
        if (e.target === e.currentTarget) {
            createdUserIdRef.current = ''
            createResponseMissingIdRef.current = false
            closeForm()
        }
    }

    function toggleEditPassword() {
        if (editPassword) {
            setInputData({ ...inputData, password: '', confirm_password: '' })
        }
        setEditPassword(!editPassword)
    }

    return (
        <>
            <OperatorActionErrorModal
                error={errorModal}
                onRetry={handleRetry}
                onCancel={handleCancelErrorModal}
                onRetryWithCancelSubscription={handleRetryWithCancelSubscription}
                onMoveAndDeleteOperator={handleMoveAndDeleteOperator}
                onKeepOrganization={handleKeepOrganization}
            />
            <div onMouseDown={handleCloseForm} className="modal-overlay">
                <div className="manage-facility-form">
                    <h3 className="manage-facility-form-title">
                        {isEditing ? 'Edit User' : 'Create User'}
                    </h3>

                    <label htmlFor="first_name">First Name <span style={{ color: 'red' }}>*</span></label>
                    <InputBase className='manger-facility-input' value={inputData.first_name} onChange={handleChange} type="text" name="first_name" id="first_name" placeholder='First Name' />

                    <label htmlFor="last_name">Last Name <span style={{ color: 'red' }}>*</span></label>
                    <InputBase className='manger-facility-input' value={inputData.last_name} onChange={handleChange} type="text" name="last_name" id="last_name" placeholder='Last Name' />

                    <label htmlFor="email">Email <span style={{ color: 'red' }}>*</span></label>
                    <InputBase className='manger-facility-input' value={inputData.email} onChange={handleChange} type="text" name="email" id="email" placeholder='Email' />

                    <label htmlFor="position">Position <span style={{ color: 'red' }}>*</span></label>
                    <InputBase className='manger-facility-input' value={inputData.position} onChange={handleChange} type="text" name="position" id="position" placeholder='e.g. Admissions Director' />

                    <div style={{ marginTop: 10, marginBottom: 10 }}>
                        <FormControlLabel
                            control={<Switch checked={siteAdmin} onChange={(e) => setSiteAdmin(e.target.checked)} />}
                            label="Site Admin"
                        />
                    </div>

                    <div className="org-section">
                        <div className="org-section-header">
                            <label className="org-section-label">Organizations</label>
                            <Tooltip title="Add organization">
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={handleAddOrganization}
                                        disabled={organizations.length >= allOrganizations.length || !!extraOrgRow}
                                    >
                                        <AddCircleOutlineIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </div>

                        {organizations.length === 0 && (
                            <p className="org-empty-message">No organizations assigned</p>
                        )}

                        {organizations.map((org, index) => {
                            if (!org.organization_id && organizations.findIndex(o => !o.organization_id) !== index) return null
                            return (
                            <div key={index} className="org-row">
                                <div className="org-row-fields">
                                    <FormControl size="small" className="org-row-operator">
                                        <Select
                                            className="manger-facility-input"
                                            value={org.organization_id ? String(org.organization_id) : ''}
                                            displayEmpty
                                            disabled={!!extraOrgRow && org !== extraOrgRow}
                                            onChange={(e) => handleOrgChange(index, 'organization', e.target.value)}
                                            renderValue={(val) => {
                                                if (!val) return <span style={{ color: '#999' }}>Organization</span>
                                                const match = allOrganizations.find(o => String(o._id) === String(val))
                                                return match ? match.name : val
                                            }}
                                            sx={{ marginBottom: 0 }}
                                        >
                                            {getAvailableOrganizations(index).map(o => (
                                                <MenuItem key={String(o._id)} value={String(o._id)}>
                                                    {o.name} <span style={{ color: '#999', marginLeft: 6, fontSize: 12 }}>({o._type})</span>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {org.organization_id && org._type !== 'hospital' && (
                                        <FormControl size="small" className="org-row-role">
                                            <Select
                                                className="manger-facility-input"
                                                value={displayRole(org.role)}
                                                displayEmpty
                                                disabled={!!extraOrgRow && org !== extraOrgRow}
                                                onChange={(e) => handleOrgChange(index, 'role', e.target.value)}
                                                renderValue={(val) => displayRole(val) || <span style={{ color: '#999' }}>Role</span>}
                                                sx={{ marginBottom: 0 }}
                                            >
                                                {ROLE_OPTIONS.map(role => (
                                                    <MenuItem key={role} value={role}>{role}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    <Tooltip title="Remove">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveOrganization(index)}
                                            className="org-row-remove"
                                            disabled={!!extraOrgRow && org !== extraOrgRow}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                            )
                        })}
                    </div>

                    {((!isEditing && !createdUserIdRef.current) || editPassword) &&
                        <>
                            <label htmlFor="password">Password</label>
                            <PasswordInput autoComplete="new-password" className='manger-facility-input' value={inputData.password || ''} onChange={handleChange} name="password" id="password" placeholder='Password' />
                            <PasswordRequirements password={inputData.password} />

                            <label htmlFor="confirm_password">Confirm Password</label>
                            <PasswordInput autoComplete="new-password" className='manger-facility-input' value={inputData.confirm_password || ''} onChange={handleChange} name="confirm_password" id="confirm_password" placeholder='Confirm Password' />
                        </>
                    }
                    {isEditing &&
                        <div>
                            <Button style={{ marginBottom: 10 }} onClick={toggleEditPassword}>{!editPassword ? 'Edit Password' : 'Cancel'}</Button>
                        </div>
                    }

                    {error && <p style={{ textAlign: 'center', margin: '5px 0px', color: '#E23844' }}>{error}</p>}

                    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                        <Button className='manger-facility-btn' disabled={isSubmitting || (extraOrgRow && (!extraOrgRow.organization_id || (extraOrgRow._type !== 'hospital' && !extraOrgRow.role)))} onClick={() => handleClick()}>{isSubmitting ? 'Submitting...' : 'Submit'}</Button>
                    </div>
                </div>
                {isSubmitting && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                        flexDirection: 'column',
                        gap: '20px'
                    }}>
                        <Loader loading={true} height={60} />
                        <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
                            Submitting form...
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default ManageUserForm
